import type { ShippingChargePromo } from '../../../modules/api';
import {
  PromoBanner,
  PromoPrice,
  StrikePrice,
  regularFees,
  type PromoSummary,
} from '../../../modules/shippingPromo';
import { PaymentRow } from './PaymentRow';

type Props = {
  shipping: number | null;
  /** Only for an option that bundles insurance. */
  insurance: number | null;
  showInsurance: boolean;
  handling: number | null;
  /** Only operators with sales tax enabled charge it; null/0 hides the row. */
  salesTax?: number | null;
  total: number | null;
  /** "Total paid" once the shipment is charged. */
  totalLabel?: string;
  /** Small print under the total. */
  note?: string;
  /** The promotion that priced a quote: its banner, and each row's regular
   *  figure struck through next to the one the player pays. */
  promo?: PromoSummary | null | undefined;
  /** The promotion that priced a delivery already charged: its banner and the
   *  regular shipping + handling struck through. The rows stay as charged. */
  chargedPromo?: ShippingChargePromo | null | undefined;
  /** That delivery was canceled or refunded in full: the promotion is still
   *  named, but the banner claims no saving. */
  refunded?: boolean;
};

// The page sets no text colour of its own, so the banner label takes the titles'.
const BANNER_TEXT = 'text-[var(--co-ShipCard-titleColor,#FFFFFF)]';

/** Shipping, handling and the total the player pays (partner Figma
 *  2026-10-08). Dashes until the fee quote arrives. */
export function PaymentSummary({
  shipping,
  insurance,
  showInsurance,
  handling,
  salesTax = null,
  total,
  totalLabel = 'Total',
  note,
  promo = null,
  chargedPromo = null,
  refunded = false,
}: Props) {
  const regular = chargedPromo ? regularFees(chargedPromo) : null;

  return (
    <div className="flex flex-col gap-2">
      {promo?.showBanner && <PromoBanner label={promo.label} discount={promo.discount} className={BANNER_TEXT} />}
      {chargedPromo && (
        <PromoBanner
          label={chargedPromo.label}
          discount={chargedPromo.discount}
          mode={refunded ? 'refunded' : 'charged'}
          className={BANNER_TEXT}
        />
      )}
      {regular !== null && (
        <PaymentRow label="Regular shipping + handling">
          <StrikePrice amount={regular} />
        </PaymentRow>
      )}
      <PaymentRow label="Shipping">
        <PromoPrice amount={shipping} original={promo?.shipping} />
      </PaymentRow>
      {showInsurance && (
        <PaymentRow label="Insurance">
          <PromoPrice amount={insurance} original={promo?.insurance} />
        </PaymentRow>
      )}
      <PaymentRow label="Handling">
        <PromoPrice amount={handling} original={promo?.handling} />
      </PaymentRow>
      {salesTax != null && salesTax > 0 && (
        <PaymentRow label="Sales tax">
          <PromoPrice amount={salesTax} />
        </PaymentRow>
      )}
      <span aria-hidden className="my-1 h-px w-full bg-[var(--co-ShipCard-divider,rgba(255,255,255,0.03))]" />
      <div className="flex items-center justify-between text-[14px] font-semibold leading-[20px] tracking-[-0.15px] text-[var(--co-ShipCard-titleColor,#FFFFFF)]">
        <span>{totalLabel}</span>
        <span className="tabular-nums">
          <PromoPrice amount={total} original={promo?.total} />
        </span>
      </div>
      {note && <p className="m-0 text-[12px] leading-[16px] text-[var(--co-ShipCard-mutedText,#6D707C)]">{note}</p>}
    </div>
  );
}
